import React, { useState, useEffect } from "react";
import { FileText } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { CampaignTypeCard } from "../campaigns/CampaignTypeCard";
import { CAMPAIGN_TYPES } from "../../config/campaignTypes";
import { startCampaignWizardTour } from "../common/CampaignWizardTour";
import { tutorialKeyForCampaignType } from "@/config/pageTutorials";

interface Props {
  name: string;
  setName: (v: string) => void;
  campaignType: string;
  setCampaignType: (v: string) => void;
  onNext: () => void;
  onCancel?: () => void;
  nameConflictError?: string | null;
  checkingCampaignName?: boolean;
}

const StepInfoBasic: React.FC<Props> = ({
  name,
  setName,
  campaignType,
  setCampaignType,
  onNext,
  onCancel,
  nameConflictError,
  checkingCampaignName,
}) => {
  const [errors, setErrors] = useState<{ name?: string; campaignType?: string }>({});

  const validate = () => {
    const newErrors: { name?: string; campaignType?: string } = {};
    if (!name || !name.trim()) newErrors.name = "El nombre es requerido.";
    if (!campaignType || !campaignType.trim()) newErrors.campaignType = "Selecciona un tipo de campaña.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const triggerTour = () => {
      return setTimeout(() => {
        startCampaignWizardTour(() => {
          localStorage.setItem('wizard_tour_seen', 'true');
        });
      }, 100);
    };

    let timeoutId: NodeJS.Timeout;

    // Verificar si ya vio el tour de campañas
    const hasSeenWizardTour = localStorage.getItem('wizard_tour_seen');
    if (!hasSeenWizardTour) {
      timeoutId = triggerTour();
    }

    const handleRestart = (e: Event) => {
      if (e instanceof CustomEvent && e.detail) e.detail.handled = true;
      console.log('🔄 [StepInfoBasic] Evento restart-local-tour recibido!');
      localStorage.removeItem('wizard_tour_seen');
      localStorage.removeItem('marketing_tour_stage1_seen');
      localStorage.removeItem('marketing_tour_stage2_seen');
      localStorage.removeItem('marketing_tour_stage3_seen');
      localStorage.removeItem('marketing_tour_stage4_seen');
      localStorage.removeItem('marketing_tour_step3_seen');
      localStorage.removeItem('marketing_tour_step4_seen');
      localStorage.removeItem('marketing_tour_step5_seen');
      localStorage.removeItem('marketing_tour_create_template_seen');
      localStorage.removeItem('renewal_tour_stage1_seen');
      localStorage.removeItem('renewal_tour_stage2_seen');
      localStorage.removeItem('renewal_tour_stage_columns_seen');
      localStorage.removeItem('renewal_tour_stage_scheduling_seen');
      localStorage.removeItem('renewal_tour_stage_reminders_seen');
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = triggerTour();
    };

    window.addEventListener('restart-local-tour', handleRestart);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('restart-local-tour', handleRestart);
    };
  }, []);

  const handleNext = () => {
    if (validate()) {
      onNext();
    }
  };


  return (
    <Card className="mt-6 relative rounded-2xl border-none shadow-lg bg-white overflow-hidden ring-1 ring-black/5">
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
      <CardContent className="space-y-8 p-8 pl-10">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center shadow-lg">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Información básica</h2>
            <p className="text-sm text-gray-500 mt-0.5">Define los datos principales de tu nueva campaña</p>
          </div>
        </div>

        {/* Campaign Type Cards (Moved to top) */}
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-semibold text-gray-700">Tipo de campaña</Label>
            <p className="text-xs text-gray-500 mt-1">Selecciona el tipo que mejor se adapte al objetivo de tu campaña</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CAMPAIGN_TYPES.map((type) => (
              <CampaignTypeCard
                key={type.id}
                id={`tour-card-${type.id.replace(/\s+/g, '-')}`}
                type={type}
                isSelected={campaignType === type.id}
                onSelect={(id) => {
                  setCampaignType(id);
                  if (errors.campaignType) setErrors(prev => ({ ...prev, campaignType: undefined }));
                }}
                hasError={!!errors.campaignType}
                compact
                tutorialKey={tutorialKeyForCampaignType(type.id)}
              />
            ))}
          </div>

          {errors.campaignType && (
            <p className="text-xs text-red-600 font-medium mt-2">{errors.campaignType}</p>
          )}
        </div>

        {/* Campaign Name Input (Moved below cards) */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-700">Nombre de la campaña</Label>
          <Input
            id="tour-input-name"
            aria-describedby={errors.name || nameConflictError ? "name-help name-error" : "name-help"}
            aria-invalid={!!errors.name || !!nameConflictError}
            className={`h-12 text-base rounded-xl border-gray-200 focus:border-teal-400 focus:ring-teal-200 transition-all ${errors.name || nameConflictError ? "border-red-400 focus:border-red-400 focus:ring-red-200" : ""}`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
            }}
            placeholder="Ej: Promo Clientes VIP o Renovación Seguros"
          />
          {(errors.name || nameConflictError) && (
            <p id="name-error" className="text-xs text-red-600 font-medium">{errors.name || nameConflictError}</p>
          )}
          <p id="name-help" className="text-xs text-gray-500">
            Elige un nombre claro y descriptivo que te ayude a identificar esta campaña fácilmente.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-4 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={onCancel}
            className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-11 rounded-xl"
          >
            ✕ Cancelar
          </Button>
          <Button
            onClick={handleNext}
            disabled={!!checkingCampaignName}
            className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-8 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95"
          >
            {checkingCampaignName ? "Verificando…" : "Siguiente →"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StepInfoBasic;
